/**
 * TopVpsIndicator — компактный значок статуса VPS в топ-баре приложения.
 *
 * Заменяет полноразмерный EnvironmentBanner: статус показывается одной
 * иконкой (pulse) и опционально хостом. По клику — модалка с VpsPanel
 * для подключения. Если env не "vps" — рисуем нейтральный значок
 * sandbox/local с подписью.
 *
 * Не теряем функциональность подключения, но не съедаем высоту чата.
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, FolderOpen, Server, X } from "lucide-react";
import { cn } from "../../lib/cn";
import { vpsStatus, isPaidPlan } from "../../lib/api";
import { VpsPanel } from "../vps/VpsPanel";
import { getEnvironment, setEnvironment } from "../onboarding/EnvironmentPicker";
import { PulseIndicator } from "./PulseIndicator";

const SUBSCRIBE_URL = "https://account.aicores.ru/subscribe?product=ide";

interface VpsLive {
  connected: boolean;
  host?: string;
  user?: string;
  loading: boolean;
}

export function TopVpsIndicator() {
  const [env, setEnvState] = useState(getEnvironment());
  const [vps, setVps] = useState<VpsLive>({ connected: false, loading: env === "vps" });
  const [showConnect, setShowConnect] = useState(false);
  const [checkingPlan, setCheckingPlan] = useState(false);

  // Клик по кнопке VPS: бесплатных уводим на оплату, платным — открываем подключение.
  async function handleVpsClick() {
    if (checkingPlan) return;
    setCheckingPlan(true);
    try {
      const paid = await isPaidPlan();
      if (!paid) {
        // Free-тариф: подключение своего сервера — платная фича. Ведём на оплату.
        window.location.href = SUBSCRIBE_URL;
        return;
      }
      // Платный: переключаем окружение в VPS и открываем модалку подключения.
      if (getEnvironment() !== "vps") {
        setEnvironment("vps");
        setEnvState("vps");
      }
      setShowConnect(true);
    } finally {
      setCheckingPlan(false);
    }
  }

  useEffect(() => {
    if (env !== "vps") return;
    let cancelled = false;

    async function check() {
      try {
        const s = (await vpsStatus()) as Record<string, unknown>;
        if (cancelled) return;
        setVps({
          connected: Boolean(s.connected || s.active),
          host: typeof s.host === "string" ? s.host : undefined,
          user: typeof s.user === "string" ? s.user : undefined,
          loading: false,
        });
      } catch {
        if (cancelled) return;
        setVps({ connected: false, loading: false });
      }
    }
    check();
    const id = setInterval(check, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [env]);

  if (!env) return null;

  // Sandbox / local — показываем бейдж среды + ОТДЕЛЬНУЮ кнопку «Подключить VPS».
  // Кнопка для free-юзеров ведёт на оплату, для платных — открывает подключение.
  if (env === "sandbox" || env === "local") {
    const Icon = env === "sandbox" ? Globe : FolderOpen;
    const label = env === "sandbox" ? "Web sandbox" : "Local";
    return (
      <>
        <span
          className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[11px] font-mono text-fg-subtle bg-bg-subtle/50 border border-border/40"
          title={label}
        >
          <Icon className="size-3.5" />
          {label}
        </span>
        <button
          type="button"
          onClick={handleVpsClick}
          disabled={checkingPlan}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] font-medium border border-accent-500/40 bg-accent-500/10 text-accent-500 hover:bg-accent-500/20 transition-colors disabled:opacity-60"
          title="Подключить свой сервер (VPS) — запуск кода, деплой, без лимитов"
        >
          <Server className="size-3.5" />
          <span className="hidden md:inline">{checkingPlan ? "Проверяю…" : "Подключить VPS"}</span>
        </button>

        <AnimatePresence>
          {showConnect && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setShowConnect(false)}
            >
              <motion.div
                initial={{ scale: 0.96, y: 8 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 8 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-bg-card border border-border/60 rounded-modal p-6 max-w-lg w-full space-y-4 shadow-card"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-h2">Подключение к VPS</h3>
                  <button
                    type="button"
                    onClick={() => setShowConnect(false)}
                    className="size-8 grid place-items-center rounded-lg text-fg-muted hover:text-fg hover:bg-bg-subtle"
                    aria-label="Закрыть"
                  >
                    <X className="size-4" />
                  </button>
                </div>
                <VpsPanel
                  autoShowForm
                  onConnected={() => {
                    setShowConnect(false);
                    setVps((v) => ({ ...v, loading: true }));
                    vpsStatus()
                      .then((st) => {
                        const o = st as Record<string, unknown>;
                        setVps({
                          connected: Boolean(o.connected || o.active),
                          host: typeof o.host === "string" ? o.host : undefined,
                          user: typeof o.user === "string" ? o.user : undefined,
                          loading: false,
                        });
                      })
                      .catch(() => setVps({ connected: false, loading: false }));
                  }}
                />
                <div className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
                  <Server className="size-3" /> подключение через SSH
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  // VPS режим
  const status = vps.loading ? "loading" : vps.connected ? "ok" : "warn";
  const isDisconnected = !vps.loading && !vps.connected;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowConnect(true)}
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-[11px] font-mono transition-colors border",
          isDisconnected
            ? "bg-warning/10 border-warning/30 text-warning hover:bg-warning/20"
            : vps.connected
              ? "bg-success/10 border-success/30 text-success hover:bg-success/20"
              : "bg-bg-subtle/50 border-border/40 text-fg-muted hover:bg-bg-subtle",
        )}
        title={
          vps.loading
            ? "Проверяю VPS…"
            : vps.connected
              ? `VPS: ${vps.user || ""}@${vps.host || ""}`
              : "VPS не подключён — нажмите для подключения"
        }
      >
        <PulseIndicator status={status} />
        <span className="hidden md:inline">
          {vps.loading ? "VPS…" : vps.connected ? `${vps.host || "VPS"}` : "VPS off"}
        </span>
      </button>

      <AnimatePresence>
        {showConnect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowConnect(false)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-bg-card border border-border/60 rounded-modal p-6 max-w-lg w-full space-y-4 shadow-card"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-h2">Подключение к VPS</h3>
                <button
                  type="button"
                  onClick={() => setShowConnect(false)}
                  className="size-8 grid place-items-center rounded-lg text-fg-muted hover:text-fg hover:bg-bg-subtle"
                  aria-label="Закрыть"
                >
                  <X className="size-4" />
                </button>
              </div>
              <VpsPanel
                autoShowForm
                onConnected={() => {
                  setShowConnect(false);
                  setVps((v) => ({ ...v, loading: true }));
                  vpsStatus()
                    .then((s) => {
                      const o = s as Record<string, unknown>;
                      setVps({
                        connected: Boolean(o.connected || o.active),
                        host: typeof o.host === "string" ? o.host : undefined,
                        user: typeof o.user === "string" ? o.user : undefined,
                        loading: false,
                      });
                    })
                    .catch(() => setVps({ connected: false, loading: false }));
                }}
              />
              <div className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
                <Server className="size-3" /> подключение через SSH
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
